import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useStore } from 'zustand';
import { TextButton } from '@/components/ui/primitives';
import { useUI } from '@/state/store';
import { useTwin } from '../store';
import { sceneStore } from '../scene/sceneStore';
import { FACILITY_JOURNEY, useFacilityTour } from './facilityTour';
import { twinActionClass } from '../ui/hud/styles';

export function FacilityControls() {
  const asset = useTwin((s) => s.asset),
    ready = useTwin((s) => s.loadStatus === 'ready');
  const model = asset?.procedural?.kind === 'facility' ? asset.procedural : null;
  const interior = model?.interior;
  const root = useStore(sceneStore, (s) => s.root);
  const { openAsset, step, setOpenAsset, setStep } = useFacilityTour();
  const navigate = useNavigate();
  const selected = useUI((s) => s.selection.subsystemId);
  const flow = useTwin((s) => s.activeSignalPathId === 'facility-flow');
  const journey = step === null ? null : FACILITY_JOURNEY[step];
  // A navigation outside the showcase ends the tour; it never redirects users back.
  useEffect(() => {
    if (ready && journey && model?.facilityId !== journey.site) setStep(null);
  }, [ready, model?.facilityId, journey, setStep]);
  useEffect(() => {
    if (!ready || !root || !asset || !interior || !journey || model?.facilityId !== journey.site) return;
    useUI.getState().select({ subsystemId: journey.room });
    setOpenAsset(journey.room ? asset.id : null);
    useTwin.getState().setViewMode('solid');
    useTwin.getState().setSignalPath(journey.room ? 'facility-flow' : null);
    useTwin
      .getState()
      .requestCamera(
        journey.room ? { subsystemId: journey.room, durationMs: 1800 } : { poseId: 'main', durationMs: 1600 },
      );
  }, [asset, interior, journey, model?.facilityId, ready, root, setOpenAsset]);
  if (!asset || !model || !interior) return null;
  const open = openAsset === asset.id;
  const go = (index: number) => {
    const next = FACILITY_JOURNEY[index];
    if (next.site !== model.facilityId) {
      // Set the route before the next asset settles. The step is applied after bindings resolve.
      useTwin.getState().setLoading();
      navigate(`/twin/${encodeURIComponent(`facility:${next.site}`)}`);
    }
    setStep(index);
  };
  const room = (id: string) => {
    setStep(null);
    setOpenAsset(asset.id);
    useTwin.getState().setViewMode('solid');
    useUI.getState().select({ subsystemId: id || null });
    useTwin
      .getState()
      .requestCamera(
        id ? { subsystemId: id, durationMs: 1500 } : { poseId: 'facility-interior', durationMs: 1500 },
      );
  };
  const close = () => {
    setStep(null);
    setOpenAsset(null);
    useUI.getState().select({ subsystemId: null });
    useTwin.getState().setSignalPath(null);
    useTwin.getState().requestCamera({ poseId: 'main', durationMs: 1600 });
  };
  return (
    <div
      className="w-full border-b border-hairline pb-1 text-[11px] text-ink-2"
      data-testid="facility-controls"
    >
      {interior && (
        <>
          <div className="mt-1 flex flex-wrap items-center gap-2" aria-label="Facility interior controls">
            <TextButton className={twinActionClass} onClick={() => (open ? close() : room(''))}>
              {open ? 'Close interior' : 'Explore interior'}
            </TextButton>
            <select
              aria-label="Facility room"
              value={interior.rooms.some((r) => r.id === selected) ? selected! : ''}
              onChange={(e) => room(e.target.value)}
              className="rounded border border-hairline bg-panel px-2 py-1"
            >
              <option value="">Whole interior</option>
              {interior.rooms.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label}
                </option>
              ))}
            </select>
            <label className="flex items-center gap-1">
              <input
                type="checkbox"
                checked={flow}
                onChange={(e) => {
                  if (e.target.checked) setOpenAsset(asset.id);
                  useTwin.getState().setSignalPath(e.target.checked ? 'facility-flow' : null);
                }}
              />{' '}
              Show data flow
            </label>
            <TextButton className={twinActionClass} onClick={() => go(0)}>
              Wallops → Suitland tour
            </TextButton>
            <span className="text-amber-200">Illustrative interiors · no live telemetry</span>
          </div>
          {journey && (
            <div
              className="mt-2 rounded border border-accent/30 bg-overlay px-3 py-2"
              role="region"
              aria-label="Facility journey"
            >
              <div className="flex items-center justify-between gap-2">
                <strong className="text-accent">
                  {(step ?? 0) + 1} / {FACILITY_JOURNEY.length} · {journey.title}
                </strong>
                <div className="flex gap-1">
                  <TextButton disabled={step === 0} onClick={() => go((step ?? 0) - 1)}>
                    Previous
                  </TextButton>
                  <TextButton
                    onClick={() =>
                      step === FACILITY_JOURNEY.length - 1 ? setStep(null) : go((step ?? 0) + 1)
                    }
                  >
                    {step === FACILITY_JOURNEY.length - 1 ? 'Finish' : 'Next'}
                  </TextButton>
                  <TextButton onClick={() => setStep(null)}>End tour</TextButton>
                </div>
              </div>
              <p className="mt-1 max-w-3xl">{journey.text}</p>
            </div>
          )}
          <details className="mt-1 px-1 text-ink-3">
            <summary className="cursor-pointer">Interior reference</summary>
            <p className="mt-1 max-w-3xl">
              These rooms fit within a public mapped footprint. Their placement, dimensions, cabinet counts
              and cable routes are illustrative. Building use and actual floor plans have not been verified.
              On-site versus cloud processing is not asserted. Select equipment for its function.
            </p>
            <a className="underline" href={interior.sourceUrl} target="_blank" rel="noreferrer">
              {interior.sourceTitle}
            </a>
          </details>
        </>
      )}
    </div>
  );
}
